import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import SFBackPageHeader from '@web/src/components/primatives/sf-back-page-header/sf-back-page-header'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import GroupForm from '@web/src/features/group/group-form/group-form'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  CreateGroupInput,
  CreateGroupMutation,
  CreateGroupMutationVariables,
  CreateGroupQuery,
} from 'types/graphql'

const CREATE_GROUP_QUERY: TypedDocumentNode<CreateGroupQuery> = gql`
  query CreateGroupQuery {
    groupTags {
      id
      name
    }
  }
`

const CREATE_GROUP_MUTATION: TypedDocumentNode<CreateGroupMutation, CreateGroupMutationVariables> = gql`
  mutation CreateGroupMutation($input: CreateGroupInput!) {
    createGroup(input: $input) {
      id
      slug
    }
  }
`

export const Route = createFileRoute('/(auth)/(main-layout)/group/create')({
  component: CreateGroupPage,
  head: () =>
    seo({
      title: `Create Group | ${env.VITE_APP_NAME}`,
      description: 'Create a new Group entry',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loader: async ({ context }) => {
    const { queryRef } = await queryAndPreload(context, CREATE_GROUP_QUERY)
    return { queryRef }
  },
})

function CreateGroupPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const router = useRouter()
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<CreateGroupQuery>)
  const isInitialLoading = networkStatus === NetworkStatus.loading

  const [createGroup] = useMutation<CreateGroupMutation, CreateGroupMutationVariables>(CREATE_GROUP_MUTATION, {
    onCompleted: (result) => {
      toast.success(t('Group created successfully'))
      navigate({ to: '/group/$groupIdOrSlug', params: { groupIdOrSlug: result.createGroup.slug } } as never)
      setShowSpinnerValue(false)
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
      setShowSpinnerValue(false)
    },
  })

  const onSave = (input: CreateGroupInput) => {
    setShowSpinnerValue(true)
    createGroup({ variables: { input } })
  }

  const handleCancel = () => {
    router.history.back()
  }

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  const groupTags = data?.groupTags ?? null

  if (!groupTags || groupTags.length === 0) {
    return <ErrorAlert error={new Error(t('Group tags not found'))} />
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-2 pt-4">
      <div className="flex w-full flex-col gap-4">
        <SFBackPageHeader lowerText={t('Set up your new group')} onBackClick={handleCancel} title={t('Create Group')} />
        <GroupForm groupTags={groupTags} onSave={onSave} showSpinner={showSpinner} />
      </div>
    </div>
  )
}
